/**
 * Limites de dépôt de documents dans le chat de l'assistant — source UNIQUE.
 *
 * Décision produit (#2066) : 50 documents par message, le même nombre sur le
 * web et dans les add-ins Word et Outlook (2026-10-15) ; 50 Mo par fichier.
 * Aucun plafond de poids total par message (décision du 2026-10-15) : le
 * serveur ne l'a jamais appliqué, seul le composeur web le contrôlait, et les
 * add-ins Word et Outlook n'en avaient pas. Le chat sert à joindre un petit
 * lot ; au-delà, le volume passe par un dossier (onglet Pièces), ce que dit la
 * phrase de refus ci-dessous.
 *
 * Ces nombres vivaient en sept copies (composeur web, toast en dur, libellé,
 * service de stockage, deux configurations multer, documentation) qui avaient
 * déjà divergé. Lecteurs : le composeur web (`utils/fileProcessing.ts`), le
 * service de stockage des pièces jointes et les deux routes de dépôt du
 * backend, la validation de la trame `user_message`, et le plafond de pièces
 * jointes des add-ins Word (mode Édition v3) et Outlook (`MAX_SELECTED_DOCUMENTS`).
 *
 * La phrase de refus est ici pour la même raison : le composeur et le
 * serveur doivent dire la même chose, mot pour mot, à l'avocat.
 */
export const CHAT_ATTACHMENT_LIMITS = {
  /** Documents déposés dans un même message. */
  maxFilesPerMessage: 50,
  /** Poids d'un fichier, en octets (50 Mo). */
  maxFileSizeBytes: 50 * 1024 * 1024,
} as const;

/**
 * Refus DÉFINITIFS d'un dépôt : codes d'erreur que la route de dépôt du chat
 * rend (champ `error` de la réponse HTTP 400) quand renvoyer le MÊME fichier
 * échouera toujours. Le composeur web s'en sert pour dire la cause à l'avocat
 * au lieu de l'inviter à réessayer (#2066) ; tout autre échec de dépôt est
 * tenu pour passager.
 *
 * Les valeurs sont celles du catalogue d'erreurs du backend
 * (`apps/backend/utils/errors/errorCodes.ts`), qui ne retire jamais un code
 * déployé.
 */
export const CHAT_ATTACHMENT_REFUSAL_CODES = {
  /** Le fichier dépasse `CHAT_ATTACHMENT_LIMITS.maxFileSizeBytes`. */
  fileTooLarge: 'FILE_TOO_LARGE',
  /** L'extension du fichier n'est pas de celles que le serveur sait lire. */
  unsupportedFormat: 'UNSUPPORTED_FORMAT',
} as const;

export type ChatAttachmentRefusalCode =
  typeof CHAT_ATTACHMENT_REFUSAL_CODES[keyof typeof CHAT_ATTACHMENT_REFUSAL_CODES];

const OCTETS_PAR_MO = 1024 * 1024;
const OCTETS_PAR_GO = 1024 * OCTETS_PAR_MO;

function ecrireLaTaille(octets: number, arrondi: (valeur: number) => number): string {
  // Deux décimales en Go (« 1,03 Go »), une en Mo (« 12,5 Mo ») ; les zéros
  // de fin tombent d'eux-mêmes (« 50 Mo », « 1 Go »).
  const arrondir = (unite: number, pas: number) => arrondi((octets / unite) * pas) / pas;
  const enMo = arrondir(OCTETS_PAR_MO, 10);
  // L'unité se décide APRÈS l'arrondi : un octet sous 1 Go s'arrondit à
  // 1024 Mo, qui doit s'écrire « 1 Go ».
  const enGo = octets >= OCTETS_PAR_GO || enMo >= 1024;
  const valeur = enGo ? arrondir(OCTETS_PAR_GO, 100) : enMo;
  return `${String(valeur).replace('.', ',')} ${enGo ? 'Go' : 'Mo'}`;
}

/**
 * Taille lisible par l'avocat : « 50 Mo », « 12,5 Mo », « 1 Go », « 1,03 Go »
 * (virgule décimale, arrondi au plus proche).
 */
export function formatChatAttachmentSize(bytes: number): string {
  return ecrireLaTaille(bytes, Math.round);
}

/**
 * Même écriture, arrondie VERS LE HAUT : pour un poids qui DÉPASSE une limite.
 * Arrondi au plus proche, un octet de trop s'écrirait comme la limite
 * elle-même (« 50 Mo, maximum 50 Mo »), ce qui ne se comprend pas.
 */
export function formatChatAttachmentSizeRoundedUp(bytes: number): string {
  return ecrireLaTaille(bytes, Math.ceil);
}

/**
 * Phrase affichée à l'avocat quand un message porte trop de documents déposés.
 * Même texte dans le toast du composeur et dans la trame de refus du serveur.
 */
export function describeChatAttachmentCountLimit(): string {
  return `Vous pouvez joindre ${CHAT_ATTACHMENT_LIMITS.maxFilesPerMessage} documents au maximum par message. `
    + 'Pour en déposer davantage, créez un dossier et ajoutez-y vos pièces.';
}

